type SectorBackgroundId =
  | "occupied-skyline"
  | "core-perimeter"
  | "moon"
  | "drone-fields"

type SectorOverlayType =
  | "toxic"
  | "void"
  | "city"
  | "snow"
  | "forest"
  | "reactor"
  | "frozen-night"
  | "moon"

interface SectorBackgroundLayer {
  key: string
  assetPath: string
  yOffset?: number
  renderHeight?: number
  /** Godot: motion_scale.x */
  parallaxSpeed?: number
  /** Godot: motion_scale.y */
  motionScaleY?: number
  /** Godot: motion_mirroring.x */
  motionMirroringX?: number
  /** Godot: motion_mirroring.y */
  motionMirroringY?: number
  /** Godot: motion_offset.x */
  motionOffsetX?: number
  /** Godot: motion_offset.y */
  motionOffsetY?: number
  /** Continuous auto-scroll speed independent of camera/player movement. */
  autoscrollX?: number
  autoscrollY?: number
  /** When true, this layer renders in front of the player for a depth/closeness effect. */
  foreground?: boolean
  /** Optional alpha override. Background layers should default to fully opaque. */
  alpha?: number
}

interface SectorBackgroundDefinition {
  id: SectorBackgroundId
  overlayType?: SectorOverlayType
  layers: SectorBackgroundLayer[]
}

export const SECTOR_BACKGROUNDS: Record<
  SectorBackgroundId,
  SectorBackgroundDefinition
> = {
  "occupied-skyline": {
    id: "occupied-skyline",
    overlayType: "city",
    layers: [
      {
        key: "bg_occupied_skyline_0",
        assetPath: "backgrounds/sectors/occupied-skyline/layer-0.webp",
        parallaxSpeed: 0.05,
      },
      {
        key: "bg_occupied_skyline_1",
        assetPath: "backgrounds/sectors/occupied-skyline/layer-1.webp",
        yOffset: 72,
        renderHeight: 180,
        parallaxSpeed: 0.2,
      },
      {
        key: "bg_occupied_skyline_2",
        assetPath: "backgrounds/sectors/occupied-skyline/layer-2.webp",
        yOffset: 540,
        renderHeight: 540,
        parallaxSpeed: 1.2,
      },
    ],
  },
  "core-perimeter": {
    id: "core-perimeter",
    overlayType: "reactor",
    layers: [
      {
        key: "bg_alien_reactor_0",
        assetPath: "backgrounds/sectors/core-perimeter/layer-0.webp",
        parallaxSpeed: 0.05,
      },
      {
        key: "bg_alien_reactor_1",
        assetPath: "backgrounds/sectors/core-perimeter/layer-1.webp",
        yOffset: 72,
        renderHeight: 180,
        parallaxSpeed: 0.2,
      },
      {
        key: "bg_alien_reactor_2",
        assetPath: "backgrounds/sectors/core-perimeter/layer-2.webp",
        yOffset: 540,
        renderHeight: 540,
        parallaxSpeed: 0.8,
      },
    ],
  },
  moon: {
    id: "moon",
    overlayType: "moon",
    layers: [
      {
        key: "bg_moon_0",
        assetPath: "backgrounds/sectors/moon/layer-0.webp",
        yOffset: 270,
        parallaxSpeed: 0.05,
      },
      {
        key: "bg_moon_1",
        assetPath: "backgrounds/sectors/moon/layer-1.webp",
        yOffset: 195,
        renderHeight: 300,
        parallaxSpeed: 0.2,
      },
      {
        key: "bg_moon_2",
        assetPath: "backgrounds/sectors/moon/layer-2.webp",
        yOffset: 340,
        renderHeight: 180,
        parallaxSpeed: 0.8,
      },
    ],
  },
  "drone-fields": {
    id: "drone-fields",
    overlayType: "forest",
    layers: [
      {
        key: "bg_drone_fields_0",
        assetPath: "backgrounds/sectors/drone-fields/layer-0.webp",
        parallaxSpeed: 0.05,
      },
      {
        key: "bg_drone_fields_1",
        assetPath: "backgrounds/sectors/drone-fields/layer-1.webp",
        yOffset: 72,
        renderHeight: 180,
        parallaxSpeed: 0.2,
      },
      {
        key: "bg_drone_fields_2",
        assetPath: "backgrounds/sectors/drone-fields/layer-2.webp",
        yOffset: 540,
        renderHeight: 540,
        parallaxSpeed: 0.8,
      },
    ],
  },
}

export const ALL_SECTOR_BACKGROUND_LAYERS: SectorBackgroundLayer[] =
  Object.values(SECTOR_BACKGROUNDS).flatMap((background) => background.layers)

export function getSectorBackground(
  sectorId: string,
): SectorBackgroundDefinition {
  // Map sector IDs to background IDs if they differ
  const idMap: Record<string, SectorBackgroundId> = {
    "occupied-skyline": "occupied-skyline",
    "lunar-corridor": "moon",
    moon: "moon",
    "drone-fields": "drone-fields",
    "core-perimeter": "core-perimeter",
  }

  const bgId = idMap[sectorId] || (sectorId as SectorBackgroundId)
  const background = SECTOR_BACKGROUNDS[bgId]

  if (!background) {
    return SECTOR_BACKGROUNDS["occupied-skyline"]
  }

  return background
}
